import clsx from 'clsx';
import dayjs from 'dayjs';
import { useField } from 'formik';
import React, { useEffect, useRef, useState } from 'react';

import CalendarIcon from '@components/Images/Calendar';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import FieldError from '../FieldError';
import Input from '../Input';
import './styles.scss';

interface CalendarFieldProps {
  className?: string;
  calendarClassName?: string;
  darkMode?: boolean;
  disabled?: boolean;
  hint?: string;
  label?: string;
  name: string;
  minDate?: Date;
  maxDate?: Date;
  locale?: string;
  showNeighboringMonth?: boolean;
  tileDisabled?: ({ date }: { date: Date }) => boolean;
}

const DatePickerField = (props: CalendarFieldProps) => {
  const { label, name, darkMode = false, ...calendarProps } = props;
  const { className } = props;
  const [field, meta, helpers] = useField(name);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Create dark theme for MUI DateCalendar
  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#F32A52', // Using your project's primary magenta color
      },
      background: {
        default: '#2F3538', // Using your project's charcoal color
        paper: '#2F3538',
      },
      text: {
        primary: '#FFFFFF',
        secondary: '#FFFFFF',
      },
    },
  });

  // Set default value to today if no value is set
  useEffect(() => {
    if (!field.value) {
      helpers.setValue(formatDate(new Date()));
    }
  }, [field.value, helpers]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };

    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    // Handle dayjs object from MUI DateCalendar
    if (value) {
      const dateValue = value.toDate();
      helpers.setValue(formatDate(dateValue));
      setIsCalendarOpen(false); // Close calendar after selection
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleInputClick = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent manual input changes, only allow calendar selection
    e.preventDefault();
  };

  return (
    <div className={className}>
      <div className="relative" ref={containerRef}>
        <div className="relative">
          <Input
            id={name}
            name={name}
            value={field.value || ''}
            onClick={handleInputClick}
            onChange={handleInputChange}
            disabled={calendarProps.disabled}
            readOnly
            type="text"
            className={className}
            inputClassName={clsx(
              'h-[50px] w-full rounded-none border border-solid border-grayscale-beige px-8 py-16 font-suisse-intl text-overline-md text-black placeholder-grayscale-gray focus:outline-none disabled:opacity-40 disabled:bg-white disabled:text-grayscale-gray'
            )}
            label={label}
            icon={
              <CalendarIcon className="absolute right-0 top-1/2 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 text-black" />
            }
          />
        </div>

        {/* Calendar Dropdown */}
        {isCalendarOpen && (
          <div className={clsx('calendar-dropdown', darkMode && 'dark-mode')}>
            <ThemeProvider theme={darkMode ? darkTheme : undefined}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  onChange={handleDateChange}
                  value={field.value ? dayjs(field.value) : dayjs()}
                  minDate={calendarProps.minDate ? dayjs(calendarProps.minDate) : undefined}
                  maxDate={calendarProps.maxDate ? dayjs(calendarProps.maxDate) : undefined}
                  disabled={calendarProps.disabled}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </div>
        )}
      </div>
      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </div>
  );
};

export default DatePickerField;
